/**
 * WalkieAudioEngine — PCM 16-bit 8000Hz playback via Web Audio API.
 *
 * Features:
 *  - Scheduled gapless playback using AudioBufferSourceNode
 *  - Volume control via GainNode
 *  - Queue management for incoming PCM chunks
 *  - Opus upgrade path (decodeOpus stub for future P1)
 */

export class WalkieAudioEngine {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private nextStartTime: number = 0;
  private queue: ArrayBuffer[] = [];
  private isPlaying: boolean = false;
  private _volume: number = 1.0;
  private _muted: boolean = false;
  private onStateChange?: () => void;

  /** PCM format constants */
  static readonly SAMPLE_RATE = 8000;
  static readonly CHANNELS = 1;
  static readonly BYTES_PER_SAMPLE = 2; // 16-bit

  constructor(onStateChange?: () => void) {
    this.onStateChange = onStateChange;
  }

  /** Initialize AudioContext — must be called after user gesture */
  async init(): Promise<void> {
    if (this.audioContext) return;
    this.audioContext = new AudioContext({ sampleRate: WalkieAudioEngine.SAMPLE_RATE });
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this._muted ? 0 : this._volume;
    this.gainNode.connect(this.audioContext.destination);
    this.nextStartTime = this.audioContext.currentTime;
  }

  /** Enqueue a PCM 16-bit 8000Hz mono buffer for playback */
  enqueuePCM(pcmBuffer: ArrayBuffer): void {
    if (!this.audioContext || !this.gainNode) {
      // Store in queue; will be processed after init
      this.queue.push(pcmBuffer);
      return;
    }
    this.scheduleBuffer(pcmBuffer);
  }

  /** Schedule a single PCM buffer for gapless playback */
  private scheduleBuffer(pcmBuffer: ArrayBuffer): void {
    if (!this.audioContext || !this.gainNode) return;

    const dataView = new DataView(pcmBuffer);
    const sampleCount = Math.floor(dataView.byteLength / WalkieAudioEngine.BYTES_PER_SAMPLE);
    if (sampleCount === 0) return;

    const audioBuffer = this.audioContext.createBuffer(
      WalkieAudioEngine.CHANNELS,
      sampleCount,
      WalkieAudioEngine.SAMPLE_RATE
    );

    const channelData = audioBuffer.getChannelData(0);
    for (let i = 0; i < sampleCount; i++) {
      // Convert 16-bit signed PCM to float [-1, 1]
      channelData[i] = dataView.getInt16(i * 2, true) / 32768;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.gainNode);

    // Gapless: schedule right after previous buffer ends
    const now = this.audioContext.currentTime;
    const startTime = Math.max(this.nextStartTime, now);
    source.start(startTime);

    const duration = sampleCount / WalkieAudioEngine.SAMPLE_RATE;
    this.nextStartTime = startTime + duration;

    this.isPlaying = true;
    this.onStateChange?.();

    source.onended = () => {
      if (this.nextStartTime <= (this.audioContext?.currentTime ?? 0) + 0.05) {
        this.isPlaying = false;
        this.onStateChange?.();
      }
    };
  }

  /** Process any queued buffers (after late init) */
  flushQueue(): void {
    while (this.queue.length > 0) {
      const buf = this.queue.shift()!;
      this.scheduleBuffer(buf);
    }
  }

  /** Set volume [0, 1] */
  setVolume(vol: number): void {
    this._volume = Math.max(0, Math.min(1, vol));
    if (this.gainNode && !this._muted) {
      this.gainNode.gain.value = this._volume;
    }
  }

  get volume(): number {
    return this._volume;
  }

  /** Mute / unmute */
  setMuted(muted: boolean): void {
    this._muted = muted;
    if (this.gainNode) {
      this.gainNode.gain.value = muted ? 0 : this._volume;
    }
  }

  get muted(): boolean {
    return this._muted;
  }

  get playing(): boolean {
    return this.isPlaying;
  }

  /** Stop all playback and reset */
  stopAll(): void {
    this.queue = [];
    this.nextStartTime = this.audioContext?.currentTime ?? 0;
    this.isPlaying = false;
    this.onStateChange?.();
  }

  /**
   * Future: Opus decoding path (P1 milestone).
   * When backend upgrades to Opus, this method will decode Opus blobs
   * using a WebAssembly decoder and enqueue the resulting PCM.
   */
  async enqueueOpus(_opusBuffer: ArrayBuffer): Promise<void> {
    // TODO P1: Implement Opus → PCM decoding
    // For now, this is a stub. The engine operates on raw PCM only.
    throw new Error('Opus decoding not yet implemented (planned for P1)');
  }

  /** Cleanup */
  destroy(): void {
    this.stopAll();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.gainNode = null;
    }
  }
}
